import { type ComponentType, useEffect, useMemo, useState } from "react";
import { type SortingState, type Updater } from "@tanstack/react-table";
import { connect } from "react-redux";
import { type RouteComponentProps, withRouter } from "react-router-dom";
import { type Dispatch, type UnknownAction } from "redux";
import { Plus, Search, ShoppingBasket, X } from "lucide-react";
import { useTranslation } from "react-i18next";

import {
  archiveTrader as archiveTraderAction,
  archiveTraderReset as resetArchiveTraderAction,
  unarchiveTrader as unarchiveTraderAction,
  unarchiveTraderReset as resetUnarchiveTraderAction,
  listTraderOrganizationActivity,
  listTraderOrganizationActivityReset,
  listTraderRepresentatives,
  listTraderRepresentativesReset,
} from "../../actions";
import { WFPButton } from "../../components/ui/button";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from "../../components/ui/input-group";
import { getCountryObjectFromCache } from "../../utils";
import { adaptTraderRepresentatives } from "../../utils/adapters";
import TraderDetailsModal from "../TraderDetailsModal";
import List, { TRADER_SORT_OPTIONS, type TraderListItem } from "./components/List";
import { type TraderOrganizationActivityQueryParams } from "./types";
import { SortMenu } from "@/components/SortMenu";
import TimeRangeTabs from "@/components/TimeRangeTabs";
import { OrgStatusTabs } from "@/components/OrgStatusTabs";
import { DEFAULT_TIME_RANGE_PERIOD, type TimeRangePeriod } from "@/constants/timeRange";
import { DEFAULT_ORG_STATUS, type OrgStatus } from "@/constants/orgStatus";
import { isExternalUser, type UserData } from "@/utils/roles";

type EntityId = number | string;

interface TraderRepresentativeListItem {
  id?: EntityId;
  organization?: {
    id?: EntityId | null;
    [key: string]: unknown;
  } | null;
  [key: string]: unknown;
}

interface ListResourceData<TItem> {
  count?: number;
  results?: TItem[];
  [key: string]: unknown;
}

interface ListResourceState<TItem> {
  data: ListResourceData<TItem>;
  errorMessage: string;
  isFetching: boolean;
}

type TraderOrganizationActivityResponse =
  | TraderListItem[]
  | {
      count?: number;
      results?: TraderListItem[];
      trader_organization_activity?: ListResourceData<TraderListItem>;
      [key: string]: unknown;
    };

interface TraderOrganizationActivityState {
  data: TraderOrganizationActivityResponse;
  errorMessage: string;
  isFetching: boolean;
}

interface RootState {
  archiveTrader: { isLoading: boolean };
  unarchiveTrader: { isLoading: boolean };
  listTraderOrganizationActivity: TraderOrganizationActivityState;
  listTraderRepresentatives: ListResourceState<TraderRepresentativeListItem>;
  userDetail: { data: UserData };
}

interface StateProps {
  isArchiving: boolean;
  isUnarchiving: boolean;
  isFetching: boolean;
  listTraderRepresentativesErrorMessage: string;
  traderOrganizationActivityErrorMessage: string;
  representatives: TraderRepresentativeListItem[];
  traders: TraderListItem[];
  currentUser: UserData;
}

interface DispatchProps {
  archiveTrader: (id: string) => Promise<unknown>;
  unarchiveTrader: (id: string) => Promise<unknown>;
  listTraderOrganizationActivity: (period: TimeRangePeriod, status: OrgStatus) => void;
  listTraderOrganizationActivityReset: () => void;
  listTraderRepresentatives: () => void;
  listTraderRepresentativesReset: () => void;
  resetArchiveTrader: () => void;
  resetUnarchiveTrader: () => void;
}

type TradersProps = StateProps & DispatchProps & Partial<RouteComponentProps>;
type RoutedTradersProps = StateProps & DispatchProps & RouteComponentProps;

type AppDispatch = Dispatch<UnknownAction>;

const EMPTY_TRADER: TraderListItem = {};

const dispatchAction = (dispatch: AppDispatch, action: unknown) =>
  dispatch(action as UnknownAction);

const getTraderId = (trader?: TraderListItem | null) =>
  trader?.id === undefined || trader.id === null ? undefined : trader.id;

const getRepresentativesForTrader = (
  representatives: TraderRepresentativeListItem[],
  traderId?: EntityId
) => {
  if (traderId === undefined) {
    return [];
  }

  return representatives.filter(representative => representative.organization?.id === traderId);
};

const pushRoute = (history: TradersProps["history"], route: string) => {
  history?.push?.(route);
};

const getTraderOrganizationActivityResults = (data: TraderOrganizationActivityResponse) => {
  const listData = Array.isArray(data) ? data : (data.trader_organization_activity ?? data);

  if (Array.isArray(listData)) {
    return listData;
  }

  return listData.results ?? [];
};

// This component is used to display the list of traders for the WFP Manager.
export const Traders = ({
  archiveTrader,
  unarchiveTrader,
  currentUser,
  history,
  location,
  isArchiving,
  isUnarchiving,
  isFetching,
  listTraderOrganizationActivity,
  listTraderOrganizationActivityReset,
  listTraderRepresentatives,
  listTraderRepresentativesErrorMessage,
  listTraderRepresentativesReset,
  representatives,
  resetArchiveTrader,
  resetUnarchiveTrader,
  traders,
  traderOrganizationActivityErrorMessage,
}: TradersProps) => {
  const { t } = useTranslation();
  const canManage = !isExternalUser(currentUser);
  const [selectedTrader, setSelectedTrader] = useState<TraderListItem | null>(null);
  const [autoOpenHandled, setAutoOpenHandled] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [sorting, setSorting] = useState<SortingState>([]);
  const [period, setPeriod] = useState<TimeRangePeriod>(DEFAULT_TIME_RANGE_PERIOD);
  const [status, setStatus] = useState<OrgStatus>(DEFAULT_ORG_STATUS);
  const selectedSort = sorting[0];
  const selectedSortOption = TRADER_SORT_OPTIONS.find(
    sortOption => sortOption.id === selectedSort?.id
  );
  const selectedSortColumn = selectedSortOption?.id || "";
  const selectedSortDirection = selectedSort?.desc ? "desc" : "asc";
  const selectedTraderId = getTraderId(selectedTrader);
  const selectedTraderRepresentatives = useMemo(
    () => getRepresentativesForTrader(representatives, selectedTraderId),
    [representatives, selectedTraderId]
  );

  useEffect(() => {
    listTraderOrganizationActivity(period, status);
  }, [listTraderOrganizationActivity, period, status]);

  useEffect(() => {
    listTraderRepresentatives();

    return () => {
      listTraderOrganizationActivityReset();
      listTraderRepresentativesReset();
      resetArchiveTrader();
      resetUnarchiveTrader();
    };
  }, [
    listTraderOrganizationActivityReset,
    listTraderRepresentatives,
    listTraderRepresentativesReset,
    resetArchiveTrader,
    resetUnarchiveTrader,
  ]);

  // Deep link support: `/wfpManager/traders?view=<traderId>` auto-opens the
  // matching trader's details modal once the list has loaded (e.g. arriving
  // from the Buyer organisation link in the Overview representatives table).
  useEffect(() => {
    if (autoOpenHandled) {
      return;
    }

    const viewId = new URLSearchParams(location?.search ?? "").get("view");

    if (!viewId) {
      return;
    }

    if (!traders.length) {
      return;
    }

    const match = traders.find(trader => String(getTraderId(trader)) === viewId);

    if (match) {
      setSelectedTrader(match);
    }

    setAutoOpenHandled(true);
    // Drop the query param so closing the modal or reloading won't reopen it.
    history?.replace?.("/wfpManager/traders");
  }, [autoOpenHandled, history, location?.search, traders]);

  const onClickAdd = () => {
    pushRoute(history, "/wfpManager/traderDetails");
  };

  const clearFilter = () => {
    setSearchTerm("");
  };

  const onSortingChange = (updater: Updater<SortingState>) => {
    setSorting(currentSorting =>
      typeof updater === "function" ? updater(currentSorting) : updater
    );
  };

  const onSortColumnChange = (sortColumn: string) => {
    setSorting([
      {
        id: sortColumn,
        desc: selectedSortDirection === "desc",
      },
    ]);
  };

  const onSortDirectionChange = (sortDirection: string) => {
    setSorting([
      {
        id: selectedSortColumn || TRADER_SORT_OPTIONS[0].id,
        desc: sortDirection === "desc",
      },
    ]);
  };

  const onClickEdit = (id?: EntityId | null) => {
    if (id === undefined || id === null) {
      return;
    }

    pushRoute(history, `/wfpManager/traderDetails/${id}`);
  };

  const onClickView = (record: TraderListItem) => {
    setSelectedTrader(record);
  };

  const closeTraderModal = () => {
    setSelectedTrader(null);
  };

  const handleArchiveTrader = async (id?: EntityId | null) => {
    if (id === undefined || id === null) {
      return undefined;
    }

    const result = await archiveTrader(String(id));
    if (result) {
      closeTraderModal();
      listTraderOrganizationActivity(period, status);
    }
    return result;
  };

  const handleUnarchiveTrader = async (id?: EntityId | null) => {
    if (id === undefined || id === null) {
      return undefined;
    }

    const result = await unarchiveTrader(String(id));
    if (result) {
      closeTraderModal();
      listTraderOrganizationActivity(period, status);
    }
    return result;
  };

  return (
    <main className="flex flex-col gap-6 py-4 px-6 sm:py-6 sm:px-8 md:py-8 md:px-10">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-x-2">
          <span className="inline-flex shrink-0 items-center justify-center size-10 rounded-lg p-2 text-green-600 bg-green-100">
            <ShoppingBasket />
          </span>
          <h1 className="font-semibold text-2xl">{`${t("traders.title")} ${t("common.in")} ${getCountryObjectFromCache().name}`}</h1>
        </div>
        {canManage && (
          <WFPButton type="button" onClick={onClickAdd}>
            <Plus className="size-4" aria-hidden="true" />
            {t("traders.newTrader")}
          </WFPButton>
        )}
      </div>
      <OrgStatusTabs
        value={status}
        onValueChange={setStatus}
        ariaLabel={t("traders.status.ariaLabel")}
        activeLabel={t("traders.status.active")}
        archivedLabel={t("traders.status.archived")}
      />
      <div className="flex flex-wrap items-center gap-2">
        <InputGroup className="w-full max-w-xs sm:w-80">
          <InputGroupAddon>
            <Search className="size-4" aria-hidden="true" />
          </InputGroupAddon>
          <InputGroupInput
            id="trader-name-input"
            type="search"
            value={searchTerm}
            className="[&::-webkit-search-cancel-button]:hidden [&::-webkit-search-decoration]:hidden"
            placeholder={t("traders.list.searchPlaceholder", "Search by name or area")}
            aria-label={t("traders.list.searchPlaceholder", "Search by name or area")}
            onChange={event => setSearchTerm(event.target.value)}
          />
          {searchTerm && (
            <InputGroupAddon align="inline-end">
              <InputGroupButton
                type="button"
                variant="ghost"
                size="icon-xs"
                aria-label={t("traders.clearFilter", "Clear filter")}
                onClick={clearFilter}
              >
                <X className="size-3.5" aria-hidden="true" />
              </InputGroupButton>
            </InputGroupAddon>
          )}
        </InputGroup>
        <SortMenu
          options={TRADER_SORT_OPTIONS}
          sortColumn={selectedSortColumn}
          sortDirection={selectedSortDirection}
          onSortColumnChange={onSortColumnChange}
          onSortDirectionChange={onSortDirectionChange}
        />
        <TimeRangeTabs
          className="ml-auto shrink-0"
          value={period}
          onValueChange={setPeriod}
          ariaLabel={t("traders.timeRange.ariaLabel")}
        />
      </div>
      <List
        isFetching={isFetching}
        listTraderRepresentativesErrorMessage={listTraderRepresentativesErrorMessage}
        traderOrganizationActivityErrorMessage={traderOrganizationActivityErrorMessage}
        onSortingChange={onSortingChange}
        onClickView={onClickView}
        searchTerm={searchTerm}
        sorting={sorting}
        traders={traders}
      />
      <TraderDetailsModal
        canManage={canManage}
        isArchiving={isArchiving}
        isUnarchiving={isUnarchiving}
        isFetchingTraderRepresentatives={isFetching}
        isOpen={selectedTrader !== null}
        onArchive={trader => handleArchiveTrader(trader.id)}
        onUnarchive={trader => handleUnarchiveTrader(trader.id)}
        onClickEdit={trader => onClickEdit(trader.id)}
        onValidate={closeTraderModal}
        representatives={selectedTraderRepresentatives}
        trader={selectedTrader ?? EMPTY_TRADER}
      />
    </main>
  );
};

// Starting from the redux state it gets data related to logged in user
export const mapStateToProps = (state: RootState): StateProps => {
  return {
    // Traders management
    isArchiving: state.archiveTrader.isLoading,
    isUnarchiving: state.unarchiveTrader.isLoading,
    isFetching:
      state.listTraderOrganizationActivity.isFetching || state.listTraderRepresentatives.isFetching,
    traderOrganizationActivityErrorMessage: state.listTraderOrganizationActivity.errorMessage,
    listTraderRepresentativesErrorMessage: state.listTraderRepresentatives.errorMessage,
    traders: getTraderOrganizationActivityResults(state.listTraderOrganizationActivity.data),
    representatives: adaptTraderRepresentatives(
      state.listTraderRepresentatives.data.results
    ) as TraderRepresentativeListItem[],
    currentUser: state.userDetail?.data ?? { user_groups: [] },
  };
};

// Maps functions to dispatch actions
export const mapDispatchToProps = (dispatch: AppDispatch): DispatchProps => {
  return {
    archiveTrader: (id: string) =>
      dispatchAction(
        dispatch,
        archiveTraderAction({}, `${id}/archive/`)
      ) as unknown as Promise<unknown>,
    unarchiveTrader: (id: string) =>
      dispatchAction(
        dispatch,
        unarchiveTraderAction({}, `${id}/unarchive/`)
      ) as unknown as Promise<unknown>,
    listTraderOrganizationActivity: (period: TimeRangePeriod, status: OrgStatus) => {
      const params: TraderOrganizationActivityQueryParams = { period, status };
      dispatchAction(dispatch, listTraderOrganizationActivity(params));
    },
    listTraderOrganizationActivityReset: () =>
      dispatchAction(dispatch, listTraderOrganizationActivityReset()),
    listTraderRepresentatives: () => dispatchAction(dispatch, listTraderRepresentatives()),
    listTraderRepresentativesReset: () =>
      dispatchAction(dispatch, listTraderRepresentativesReset()),
    resetArchiveTrader: () => dispatchAction(dispatch, resetArchiveTraderAction()),
    resetUnarchiveTrader: () => dispatchAction(dispatch, resetUnarchiveTraderAction()),
  };
};

// The component uses the redux store
const RoutedTraders = withRouter(Traders as ComponentType<RoutedTradersProps>);

export default connect(mapStateToProps, mapDispatchToProps)(RoutedTraders);
